import { useEffect, useRef, useState } from "react";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, FileSignature, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { SignaturePad, type SignaturePadHandle } from "@/components/signature-pad";

interface BootstrapData {
  documentName: string;
  bodyHtml: string;
  recipient: { name: string; email: string };
  token: string;
}

interface SignedResponse {
  success: true;
  documentName: string;
  signedAt: string;
  auditHash: string;
}

export default function PlatformSignPage() {
  const params = useParams<{ token: string }>();
  const token = params.token;

  const [boot, setBoot] = useState<BootstrapData | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(true);

  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [agree, setAgree] = useState(false);
  const [method, setMethod] = useState<"drawn" | "typed">("drawn");
  const [typedName, setTypedName] = useState("");
  const [drawnEmpty, setDrawnEmpty] = useState(true);

  const padRef = useRef<SignaturePadHandle>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<SignedResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/platform-sign/${encodeURIComponent(token)}`, { credentials: "omit" });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (!cancelled) setBootError(data?.message || "This signing link is no longer valid.");
          return;
        }
        const data = (await res.json()) as BootstrapData;
        if (cancelled) return;
        setBoot(data);
        setSignerName(data.recipient.name || "");
        setSignerEmail(data.recipient.email || "");
        setTypedName(data.recipient.name || "");
        document.title = `Sign: ${data.documentName} — Clarik`;
      } catch {
        if (!cancelled) setBootError("Couldn't load this document. Please try again later.");
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!signerName.trim()) { setSubmitError("Please enter your full legal name."); return; }
    if (!signerEmail.trim()) { setSubmitError("Please enter your email address."); return; }
    if (!agree) { setSubmitError("You must agree to the terms before signing."); return; }

    let signatureDataUrl: string | null = null;
    let typedNameOut: string | null = null;
    if (method === "drawn") {
      signatureDataUrl = padRef.current?.getDataUrl() || null;
      if (!signatureDataUrl) { setSubmitError("Please draw your signature."); return; }
    } else {
      if (!typedName.trim()) { setSubmitError("Please type your name as your signature."); return; }
      typedNameOut = typedName.trim();
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/platform-sign/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signedByName: signerName.trim(),
          signedByEmail: signerEmail.trim(),
          agree: true,
          signatureMethod: method,
          signatureDataUrl,
          typedName: typedNameOut,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data?.message || "Could not save your signature. Please try again.");
        return;
      }
      setDone(data as SignedResponse);
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (bootError || !boot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Link unavailable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground" data-testid="text-platform-sign-error">
              {bootError || "Not found."}
            </p>
            <p className="text-sm text-muted-foreground">
              If you believe this is a mistake, contact the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" /> Signed successfully
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm" data-testid="text-platform-sign-success">
              Thanks, {signerName.split(" ")[0] || "there"}. Your signature on{" "}
              <strong>{done.documentName}</strong> has been recorded.
            </p>
            <div className="rounded-md bg-gray-100 p-3 text-xs space-y-1 font-mono">
              <div>Signed at: {new Date(done.signedAt).toLocaleString()}</div>
              <div>Audit hash: {done.auditHash.slice(0, 24)}…</div>
            </div>
            <p className="text-xs text-muted-foreground">
              Save this audit reference for your records.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white border-t-4 border-t-blue-600">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded bg-blue-50 flex items-center justify-center">
              <FileSignature className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Document from</div>
              <div className="text-sm font-semibold text-gray-900">Clarik</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secure signing
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-platform-document-name">
            {boot.documentName}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review the document below, then sign at the bottom of the page.
          </p>
        </div>

        {/* Document body */}
        <Card>
          <CardContent className="p-6">
            {boot.bodyHtml ? (
              <div
                className="prose prose-sm max-w-none text-gray-800"
                dangerouslySetInnerHTML={{ __html: boot.bodyHtml }}
                data-testid="div-platform-document-body"
              />
            ) : (
              <div className="py-10 text-center text-gray-400">
                <FileSignature className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  No document body attached. Your signature below will serve as
                  acceptance of the document named above.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signing form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your signature</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signer-name">Full legal name</Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={e => setSignerName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    data-testid="input-platform-signer-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signer-email">Email address</Label>
                  <Input
                    id="signer-email"
                    type="email"
                    value={signerEmail}
                    onChange={e => setSignerEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="input-platform-signer-email"
                  />
                </div>
              </div>

              <Tabs value={method} onValueChange={v => setMethod(v as "drawn" | "typed")}>
                <TabsList className="grid grid-cols-2 w-full sm:w-auto">
                  <TabsTrigger value="drawn" data-testid="tab-platform-draw">Draw signature</TabsTrigger>
                  <TabsTrigger value="typed" data-testid="tab-platform-type">Type signature</TabsTrigger>
                </TabsList>
                <TabsContent value="drawn" className="mt-3">
                  <SignaturePad
                    ref={padRef}
                    height={180}
                    onChange={isEmpty => setDrawnEmpty(isEmpty)}
                  />
                </TabsContent>
                <TabsContent value="typed" className="mt-3 space-y-2">
                  <Input
                    value={typedName}
                    onChange={e => setTypedName(e.target.value)}
                    placeholder="Type your name as your signature"
                    className="text-2xl h-14"
                    style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}
                    data-testid="input-platform-typed-signature"
                  />
                  <p className="text-xs text-muted-foreground">
                    By typing your name above, you agree it is the legal
                    equivalent of your handwritten signature.
                  </p>
                </TabsContent>
              </Tabs>

              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="agree"
                  checked={agree}
                  onCheckedChange={v => setAgree(v === true)}
                  data-testid="checkbox-platform-agree"
                />
                <Label htmlFor="agree" className="text-sm font-normal leading-snug cursor-pointer">
                  I have read this document and agree that my electronic
                  signature is the legal equivalent of my handwritten signature.
                </Label>
              </div>

              {submitError && (
                <Alert variant="destructive" data-testid="alert-platform-submit-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={
                    submitting ||
                    !agree ||
                    !signerName.trim() ||
                    !signerEmail.trim() ||
                    (method === "drawn" && drawnEmpty) ||
                    (method === "typed" && !typedName.trim())
                  }
                  data-testid="button-platform-submit-signature"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</>
                  ) : (
                    <>Sign and submit</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        <p className="text-xs text-center text-gray-400 py-4">
          This signing session is secured by a one-time link. Your IP address
          and browser are recorded with the signature for audit purposes.
        </p>
      </div>
    </div>
  );
}
